import arabicUiCopy from "../../statics/arabicUiCopy";
import useMountedRef from "../../utils/useMountedRef";
import { flowCopy } from "../../utils/flowCopy";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  getSubscriberInfo,
  getSubscriptions
} from "../../api/settings/Settings";
import localizationStrings from "../../statics/localization";
import { appVersion } from "../../statics/config";
import { useHistory } from "react-router-dom";
import ReturnIcon from "../../assets/icons/svg/ReturnIcon";
import "./VersionInfo.scss";

const VersionInfo = () => {
  const history = useHistory();
  const mounted = useMountedRef();
  const requestVersion = useRef(0);
  const tr = localizationStrings.getLanguage() === "tr";
  const copy = localizationStrings.getLanguage() === "arb" ? arabicUiCopy.version : tr ? { username: "Kullanıcı adı", phone: "Telefon", plan: "Paket", hide: "Detayları gizle", details: "Paket detayları", storage: "Kayıt alanı" } : { username: "Username", phone: "Phone", plan: "Plan", hide: "Hide Details", details: "Plan Details", storage: "Storage" };
  const [subscriber, setSubscriber] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [errorText, setErrorText] = useState("");

  const normalizeSubscriber = (data) => {
    const payload = data?.Data ?? data?.data ?? data ?? {};

    return {
      username: payload?.Username || payload?.username || "-",
      email: payload?.EmailAddress || payload?.emailAddress || "-",
      phone:
        (payload?.Msisdn ?? payload?.msisdn) && String(payload?.Msisdn ?? payload?.msisdn) !== "0"
          ? String(payload.Msisdn ?? payload.msisdn)
          : "-",
      usedDurationLabel:
        payload?.UsedDurationQuotaLabel || payload?.usedDurationQuotaLabel || "00:00:00",
      activeDurationLabel:
        payload?.ActiveDurationQuotaLabel || payload?.activeDurationQuotaLabel || "00:00:00",
      durationQuotaPercent:
        typeof payload?.DurationQuotaPercent === "number"
          ? payload.DurationQuotaPercent
          : typeof payload?.durationQuotaPercent === "number"
          ? payload.durationQuotaPercent
          : 0
    };
  };

  const normalizeSubscriptions = (data) => {
    const payload = data?.Data ?? data?.data ?? data;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.Data)
      ? payload.Data
      : Array.isArray(payload?.data)
      ? payload.data
      : [];

    return items.map((item) => ({
      uid: item?.UId || item?.uId,
      offerTitle: item?.OfferTitle || item?.offerTitle || "-",
      offerDescription: item?.OfferDescription || item?.offerDescription || "",
      startDate: item?.StartDate || item?.startDate || "",
      endDate: item?.EndDate || item?.endDate || "",
      isActive: (item?.IsActive ?? item?.isActive) === undefined ? true : !!(item?.IsActive ?? item?.isActive)
    }));
  };

  const loadData = async () => {
    const version = ++requestVersion.current;
    setIsLoading(true); setErrorText("");
    const apply = (request, setter, normalize) => request().then(response => {
      if (response.status && response.status !== 200) throw new Error("Account data unavailable");
      if (mounted.current && version === requestVersion.current) setter(normalize(response.data));
    }).catch(() => {
      if (mounted.current && version === requestVersion.current) setErrorText(flowCopy().loadError);
    });
    await Promise.all([
      apply(getSubscriberInfo, setSubscriber, normalizeSubscriber),
      apply(getSubscriptions, setSubscriptions, normalizeSubscriptions)
    ]);
    if (mounted.current && version === requestVersion.current) setIsLoading(false);
  };

  useEffect(() => { loadData(); return () => { requestVersion.current++; }; }, []);

  const activeSubscription = useMemo(() => {
    const current = subscriptions.find((item) => item.isActive);
    return current || subscriptions[0] || null;
  }, [subscriptions]);

  const usedQuotaPercent = useMemo(() => {
    if (!subscriber) {
      return 0;
    }
    const value = Number(subscriber.durationQuotaPercent || 0);
    if (Number.isNaN(value)) {
      return 0;
    }
    if (value < 0) {
      return 0;
    }
    if (value > 100) {
      return 100;
    }
    return value;
  }, [subscriber]);

  const infoRows = [
    {
      label: copy.username,
      value: subscriber?.username || "-"
    },
    {
      label: "E-Mail",
      value: subscriber?.email || "-"
    },
    {
      label: copy.phone,
      value: subscriber?.phone || "-"
    },
    {
      label: copy.plan,
      value: activeSubscription?.offerTitle || "-"
    }
  ];

  const legacyInfoCards = [
    {
      title: localizationStrings.appVersion,
      value: appVersion
    },
    {
      title: localizationStrings.networkStatus,
      value: navigator.onLine
        ? localizationStrings.connected
        : localizationStrings.disconnected
    }
  ];

  const hasSubscriptionPeriod =
    !!activeSubscription?.startDate && !!activeSubscription?.endDate;

  return (
    <div className="settings-container version-info-container">
      <div
        className="settings-icon"
        style={{
          position: "fixed",
          insetInlineStart: `${(33/1920)*100}vw`,
          top: `calc(${(72/1920)*100}vw + ${(49/1920)*100}vw)`,
          zIndex: 5,
          margin: 0,
          gap: "10px"
        }}
        onClick={() => {
          history.push(`/dashboard/settings`);
        }}
      >
        <ReturnIcon />
        <div className="icon-settings">{localizationStrings.back}</div>
      </div>

      <div className="version-info-content">
        <div className="legacy-info-wrapper">
          {legacyInfoCards.map((card) => (
            <div key={card.title} className="legacy-info-card">
              <div className="legacy-info-title">{card.title}</div>
              <div className="legacy-info-value">{card.value}</div>
            </div>
          ))}
        </div>

        {infoRows.map((row) => (
          <div key={row.label} className="version-info-row">
            <div className="version-info-label">{row.label}</div>
            <div className="version-info-value">{row.value}</div>
            {row.label === copy.plan ? (
              <button
                type="button"
                className="plan-detail-btn"
                onClick={() => setShowPlanDetails((prev) => !prev)}
              >
                {showPlanDetails ? copy.hide : copy.details}
              </button>
            ) : null}
          </div>
        ))}

        {isLoading ? <div className="version-info-message" role="status">{flowCopy().loading}</div> : null}
        {errorText ? <div className="version-info-error" role="alert">{errorText} <button disabled={isLoading} onClick={loadData}>{flowCopy().retry}</button></div> : null}

        {showPlanDetails ? (
          <div className="plan-details-wrapper">
            <div className="quota-card">
              <div className="quota-label">{copy.storage}</div>
              <div className="quota-content">
                <div className="quota-values">
                  <div className="quota-used">{subscriber?.usedDurationLabel || "00:00:00"}</div>
                  <div className="quota-total">{subscriber?.activeDurationLabel || "00:00:00"}</div>
                </div>
                <div className="quota-bar">
                  <div className="quota-track-bg" />
                  <div
                    className="quota-fill"
                    style={{ width: `${usedQuotaPercent}%` }}
                  />
                </div>
              </div>
            </div>

            {activeSubscription ? (
              <div className="subscription-card">
                <div className="subscription-title">{activeSubscription.offerTitle}</div>
                {hasSubscriptionPeriod ? (
                  <div className="subscription-period">
                    {`${activeSubscription.startDate} - ${activeSubscription.endDate}`}
                  </div>
                ) : null}
                {activeSubscription?.offerDescription ? (
                  <div className="subscription-description">
                    {activeSubscription.offerDescription}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

      </div>
    </div>
  );
};

export default VersionInfo;
